import { Card, CardContent, CardHeader, CardTitle } from '@predoc/cow';
import { DocumentTemplateShell, TemplateEmptyState } from './TemplateShared';
import {
  TemplateSummaryCard,
  type TemplateSummaryItem,
} from './TemplateSummaryCard';
import {
  DASH,
  formatLongDate,
  formatPractitionerNameWithRole,
} from './templateUtils';
import { ProcedureNoteContent } from './ProcedureNoteContent';
import type { TemplatePatient, TemplatePractitioner } from './templateUtils';

export type ProceduresTemplatePractitioner = TemplatePractitioner;

export interface ProceduresTemplateProblemRow {
  id: string;
  name?: string;
  onsetDate?: string;
  clinicalStatus?: string;
  conditionNote?: string;
  recordedDate?: string;
}

export interface ProceduresTemplateLocation {
  name?: string;
  addressUse?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

export interface ProceduresTemplateProcedure {
  id: string;
  name?: string;
  dateOfStudy?: string;
  reason?: string;
  encounterId?: string;
  asserter?: ProceduresTemplatePractitioner;
  performer?: ProceduresTemplatePractitioner;
  conditions?: ProceduresTemplateProblemRow[];
  locations?: ProceduresTemplateLocation[];
  procedureNote?: string;
}

export interface ProceduresAndDiagnosticsTemplateProps {
  procedures: ProceduresTemplateProcedure[];
  patient?: TemplatePatient;
  filename?: string;
  generatedAt?: Date;
  pageInfo?: { current: number; total: number };
  highlightProcedureId?: string;
  reportDate?: string;
}

const formatLocation = (loc: ProceduresTemplateLocation) => {
  const cityState = [loc.city, loc.state].filter(Boolean).join(', ');
  const parts = [loc.address, cityState, loc.postalCode].filter(Boolean);
  return parts.length ? parts.join(' • ') : DASH;
};

interface DetailRowProps {
  label: string;
  children: React.ReactNode;
}

const DetailRow = ({ label, children }: DetailRowProps) => (
  <div className="flex items-start gap-5 px-5 py-1.5 last:border-b-0">
    <p className="w-24 shrink-0 text-[10px] leading-tight font-bold text-zinc-950">
      {label}
    </p>
    <div className="flex-1 text-[10px] leading-tight text-zinc-950">
      {children}
    </div>
  </div>
);

interface ProcedureSectionProps {
  procedure: ProceduresTemplateProcedure;
  index: number;
  highlightProcedureId?: string;
}

const ProcedureSection = ({
  procedure,
  highlightProcedureId,
}: ProcedureSectionProps) => {
  const isHighlighted =
    !!highlightProcedureId && procedure.id === highlightProcedureId;
  const conditions = procedure.conditions ?? [];
  const locations = procedure.locations ?? [];
  const conditionNames = conditions
    .map((condition) => condition.name)
    .filter(Boolean);
  const conditionSummary = conditionNames.join(', ');
  const hasProcedureNote = Boolean(procedure.procedureNote?.trim());
  const locationSummary =
    locations
      .map((location) =>
        [location.name, formatLocation(location)].filter(Boolean).join(' — '),
      )
      .filter(Boolean)
      .join(', ') || DASH;

  return (
    <Card
      id={`procedure-${procedure.id}`}
      tabIndex={isHighlighted ? -1 : undefined}
      className={`flex flex-col overflow-hidden rounded-sm border-2 ${
        isHighlighted ? 'border-amber-400' : 'border-gray-200'
      } bg-white p-0 pb-4 shadow-none`}
    >
      <CardHeader className="flex flex-col gap-2 rounded-none bg-slate-700 px-5 pt-5 pb-3 text-white">
        <CardTitle className="text-[13px] leading-tight font-semibold text-white uppercase">
          {procedure.name?.trim() || 'Procedure'}
        </CardTitle>
        {procedure.dateOfStudy ? (
          <p className="text-[9px] leading-tight">
            <span className="font-semibold">Service Date:</span>{' '}
            {formatLongDate(procedure.dateOfStudy)}
          </p>
        ) : null}
      </CardHeader>

      <CardContent className="mt-3 overflow-hidden bg-white p-0">
        <DetailRow label="Reason">{procedure.reason || DASH}</DetailRow>
        <DetailRow label="Asserter">
          {formatPractitionerNameWithRole(procedure.asserter)}
        </DetailRow>
        <DetailRow label="Performing">
          {formatPractitionerNameWithRole(procedure.performer)}
        </DetailRow>
        <DetailRow label="Encounter">{procedure.encounterId || DASH}</DetailRow>
        <DetailRow label="Locations">{locationSummary}</DetailRow>
        <DetailRow label="Conditions">{conditionSummary || DASH}</DetailRow>
        {hasProcedureNote ? (
          <div className="px-5 py-1.5">
            <ProcedureNoteContent procedureNote={procedure.procedureNote} />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

const buildProcedureSummaryItems = (
  procedures: ProceduresTemplateProcedure[],
): TemplateSummaryItem[] => {
  const nameCounts = new Map<string, number>();
  procedures.forEach((procedure) => {
    const name = procedure.name?.trim() || 'Procedure';
    nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
  });

  const seenNames = new Set<string>();

  return procedures.flatMap((procedure, index) => {
    const name = procedure.name?.trim();
    if (!name) return [];

    if (seenNames.has(name)) return [];
    seenNames.add(name);

    return [
      {
        id: procedure.id,
        label: `${name} (${nameCounts.get(name) ?? 1})`,
        anchorId: `procedure-${procedure.id}`,
        pageNumber: index + 2,
      },
    ];
  });
};

const buildConditionSummaryItems = (
  procedures: ProceduresTemplateProcedure[],
): TemplateSummaryItem[] => {
  const seenLabels = new Set<string>();

  return procedures.flatMap((procedure) =>
    (procedure.conditions ?? []).flatMap((condition) => {
      const name = condition.name?.trim();
      if (!name || seenLabels.has(name)) return [];
      seenLabels.add(name);

      return [
        {
          id: `${procedure.id}-${name}`,
          label: name,
        },
      ];
    }),
  );
};

export const ProceduresAndDiagnosticsTemplate = ({
  procedures,
  patient,
  filename,
  generatedAt,
  pageInfo,
  highlightProcedureId,
  reportDate,
}: ProceduresAndDiagnosticsTemplateProps) => {
  const items = procedures ?? [];
  const hasItems = items.length > 0;
  const procedureSummaryItems = buildProcedureSummaryItems(items);
  const conditionSummaryItems = buildConditionSummaryItems(items);

  return (
    <DocumentTemplateShell
      title="Procedures & Diagnostics Report"
      patient={patient}
      filename={filename}
      generatedAt={generatedAt}
      pageInfo={pageInfo}
      reportDate={reportDate}
      reportDateClassName="text-[14px] leading-tight font-medium whitespace-nowrap text-black"
    >
      <section className="flex flex-1 flex-col gap-5 px-8 py-5">
        <div className="flex gap-5">
          <TemplateSummaryCard
            title="Procedures & ancillary results"
            count={procedureSummaryItems.length}
            items={procedureSummaryItems}
            tone="green"
          />
          <TemplateSummaryCard
            title="Conditions"
            count={conditionSummaryItems.length}
            items={conditionSummaryItems}
            tone="red"
          />
        </div>
        {!hasItems ? (
          <TemplateEmptyState message="No procedures available" />
        ) : (
          items.map((procedure, index) => (
            <ProcedureSection
              key={procedure.id ?? `proc-${index}`}
              procedure={procedure}
              index={index}
              highlightProcedureId={highlightProcedureId}
            />
          ))
        )}
      </section>
    </DocumentTemplateShell>
  );
};
